/**
 * Content Markdown (spec §4.3): GFM tables plus `$…$` / `$$…$$` math rendered
 * with KaTeX. Raw HTML is NOT enabled — content is authored as Markdown only,
 * and figures go through `<Figure>` where they are sanitised.
 */
import clsx from "clsx";
import { memo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import type { Element as HastElement, ElementContent, Root } from "hast";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { useT } from "@client/i18n";
import { readableTex } from "@client/lib/readableMath";
import { useFocusableOverflow } from "@client/lib/useFocusableOverflow";
import { remarkInlineDisplayMath } from "./mathBlocks";

export interface MarkdownProps {
  children: string;
  /**
   * `prose` sizing; `compact` drops the vertical rhythm for inline use;
   * `inline` additionally collapses the wrapper and its paragraphs to
   * `display: inline`, so a one-line content string (a rubric item, an answer
   * option, a final answer) flows inside running text or a `<label>` instead
   * of opening a block of its own.
   */
  variant?: "default" | "compact" | "inline";
  className?: string;
}

const REMARK = [remarkGfm, remarkMath, remarkInlineDisplayMath];
const REHYPE = [rehypeKatex, rehypeMathOnlyHeaders];

const KATEX_ANNOTATION_ENCODING = "application/x-tex";

/**
 * Reads a hast property that `@types/hast`'s `Properties` interface does not
 * model, because it belongs to foreign (MathML) content — KaTeX's own
 * `encoding` attribute on `<annotation>` — rather than to HTML/SVG.
 */
function foreignProperty(properties: HastElement["properties"], key: string): string | undefined {
  const value = (properties as Record<string, unknown>)[key];
  return typeof value === "string" ? value : undefined;
}

function isKatexSpan(node: ElementContent): boolean {
  return (
    node.type === "element" &&
    node.tagName === "span" &&
    Array.isArray(node.properties?.className) &&
    node.properties.className.includes("katex")
  );
}

/**
 * Text content of `children`, skipping anything KaTeX rendered — its visible
 * glyphs (e.g. the "n" in `$n$`) are math, not prose, and its own accessible
 * text is recovered separately from the TeX source (see `katexSources`).
 */
function plainText(children: ElementContent[]): string {
  let text = "";
  for (const child of children) {
    if (child.type === "text") {
      text += child.value;
    } else if (child.type === "element" && !isKatexSpan(child)) {
      text += plainText((child.children ?? []) as ElementContent[]);
    }
  }
  return text;
}

/**
 * The original TeX source of every KaTeX span inside `children`, recovered
 * from the `<annotation encoding="application/x-tex">` node KaTeX embeds in
 * its MathML output for exactly this purpose (rehype-katex has already
 * replaced the authored `$…$` with the rendered markup by the time a
 * react-markdown component sees it, so the raw Markdown is not available
 * here — the annotation is).
 */
function katexSources(children: ElementContent[]): string[] {
  const sources: string[] = [];
  const visit = (node: ElementContent): void => {
    if (node.type !== "element") return;
    if (
      node.tagName === "annotation" &&
      foreignProperty(node.properties, "encoding") === KATEX_ANNOTATION_ENCODING
    ) {
      sources.push(plainText((node.children ?? []) as ElementContent[]));
      return;
    }
    for (const child of (node.children ?? []) as ElementContent[]) visit(child);
  };
  for (const child of children) visit(child);
  return sources;
}

function mathOnlyLabel(node: HastElement): string | undefined {
  const children = node.children;
  if (plainText(children).trim() !== "") return undefined;
  const sources = katexSources(children);
  if (sources.length === 0) return undefined;
  return sources.map(readableTex).join(" ");
}

function hasClass(node: ElementContent, name: string): node is HastElement {
  return (
    node.type === "element" &&
    Array.isArray(node.properties?.className) &&
    node.properties.className.includes(name)
  );
}

/** Drops KaTeX's clipped MathML twin and un-hides its visible glyphs. */
function exposeKatexGlyphs(node: HastElement): void {
  node.children = node.children.filter((child) => !hasClass(child, "katex-mathml"));
  for (const child of node.children) {
    if (child.type !== "element") continue;
    if (hasClass(child, "katex-html")) delete child.properties.ariaHidden;
    exposeKatexGlyphs(child);
  }
}

/**
 * axe `empty-table-header`: a `<th>` whose only content is KaTeX output (e.g.
 * `| $n$ | $m_l$ |`) has no *visible* text for the rule to see — KaTeX's
 * glyphs are `aria-hidden` and its MathML twin is clipped to 1px, and the
 * rule does not credit `aria-label`. For such a header only, keep the KaTeX
 * rendering but expose its glyphs as real text (dropping the MathML twin so
 * nothing is read twice), and name the cell from its TeX source. Runs after
 * rehype-katex; headers with any prose are untouched.
 */
function rehypeMathOnlyHeaders() {
  const visit = (node: Root | HastElement): void => {
    for (const child of node.children) {
      if (child.type !== "element") continue;
      const label = child.tagName === "th" ? mathOnlyLabel(child) : undefined;
      if (label) {
        child.properties.ariaLabel = label;
        exposeKatexGlyphs(child);
      } else {
        visit(child);
      }
    }
  };
  return (tree: Root) => visit(tree);
}

export const Markdown = memo(function Markdown({
  children,
  variant = "default",
  className,
}: MarkdownProps) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);

  // The inline variant collapses to `display: inline` (it flows inside a
  // label or a line of running text) and never opens a scrollable box of its
  // own, so it never becomes a tab stop and is never scanned.
  useFocusableOverflow(ref, children, {
    enabled: variant !== "inline",
    label: t("common.scrollableContent"),
  });

  return (
    <div
      ref={ref}
      className={clsx(
        "prose prose-ink max-w-prose dark:prose-invert",
        // Inline KaTeX cannot wrap, so one long `$…$` would otherwise push the
        // whole page sideways on a phone (spec §5: "mobile single column
        // without horizontal scroll"). Keep the scroll inside the block.
        "overflow-x-auto",
        "prose-headings:font-display prose-table:text-sm",
        "prose-pre:bg-ink-100 dark:prose-pre:bg-ink-800",
        variant === "compact" && "prose-p:my-1 prose-ul:my-1 prose-ol:my-1",
        variant === "inline" && "inline prose-p:my-0 prose-p:inline",
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={REMARK} rehypePlugins={REHYPE}>
        {children}
      </ReactMarkdown>
    </div>
  );
});
